import {Brick, registerBrick, Service, ServiceRequestType} from 'olympe';
import {tap} from 'rxjs/operators';
import {
    applyOpenPrs,
    applyUserServicePeerSnapshots,
    beginUserServiceStartupSync,
    collectPeerSnapshots,
    COLLECT_MEMORIES_TIMEOUT_MS,
    MAX_EXPECTED_REPLICAS,
    noteOwnMulticast,
    recordCollectReply,
    recordUserServiceStartupMutation,
    removeFrontendFromPrLists,
    SYNC_ECHO_GRACE_MS,
    SYNC_MUTATION,
} from './sharedMemorySync';

/**
 * Service to track which users are reviewing which PRs in real-time
 */
export default class CreateUserService extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Array} inputs
     * @param {function()} forwardEvent
     * @param {function(Map)} setFrontendsCheckingAPR
     * @param {function(Map)} setFrontendIdUserLink
     * @param {function(string)} setUniqueID
     */
    update($, inputs, [forwardEvent, setFrontendsCheckingAPR, setFrontendIdUserLink, setUniqueID]) {
        const userService = new Service('userService', $);

        const backendId = crypto.randomUUID();

        // As long as a frontend is in this list, that means that he is online and subscribed to this backend
        const onlineFrontends = /** @type {Map<string, string>} */ new Map();
        // This map will contain an array of frontends for each PR. This list of frontends has to be an array, because
        // the rank is used to determine which frontend can edit the PR
        const frontendsCheckingAPR = /** @type {Map<string, string[]>} */ new Map();
        // This map will contain the link between a frontend id and its user info (user id and full name)
        const frontendIdUserLink = /** @type {Map<string, {userId: string, userFullName: string}>} */ new Map();

        // In-flight collect requests, keyed by correlation id.
        const pendingCollections = /** @type {Map<string, Object>} */ new Map();

        // Live mutations recorded during startup sync.
        beginUserServiceStartupSync(frontendsCheckingAPR);

        /**
         * Shared state for this replica. onlineFrontends stays local.
         *
         * @return {{backendId: string, frontendsCheckingAPR: Map<string, string[]>, frontendIdUserLink: Map<string, {userId: string, userFullName: string}>}}
         */
        const getSyncSnapshot = () => ({
            backendId,
            frontendsCheckingAPR,
            frontendIdUserLink,
        });

        /**
         * Handle a collect request or reply.
         *
         * @param {Object} message
         * @return {void}
         */
        const handleCollect = (message) => {
            if (message.uuid === backendId) {
                noteOwnMulticast(pendingCollections, message.correlationId);
                return;
            }
            if (message.topic === USER_SERVICE_TOPICS.COLLECT_STATE) {
                userService.multicast({
                    topic: USER_SERVICE_TOPICS.COLLECT_STATE_REPLY,
                    uuid: backendId,
                    correlationId: message.correlationId,
                    coordinatorId: message.uuid,
                    memories: getSyncSnapshot(),
                }).catch((e) => {
                    console.error('Error replying to the user service sync request:', e);
                });
                return;
            }
            recordCollectReply(pendingCollections, backendId, message);
        };

        userService.listen().pipe(
            tap(async (req) => {
                const {
                    /** @type {string} */ frontendId,
                    /** @type {string[]} */ openPrs,
                    /** @type {string} */ updatedPr,
                    /** @type {string} */ uuid,
                    /** @type {string} */ userId,
                    /** @type {string} */ userFullName,
                    /** @type {string} */ topic,
                    /** @type {string} */ frontendWhoUnsubscribed,
                    /** @type {string} */ initiator,
                } = req.body();
                switch (req.requestType()) {
                case ServiceRequestType.SUBSCRIBE:
                    try {
                        const {userId, userFullName, frontendId} = req.body();
                        // Create a subscription and update state
                        const subId = await req.notifyOn(`SubscribedPR_${frontendId}`);
                        onlineFrontends.set(subId, frontendId);
                        frontendIdUserLink.set(frontendId, {userId, userFullName});
                        recordUserServiceStartupMutation(frontendsCheckingAPR, {
                            op: SYNC_MUTATION.SET_USER_LINK,
                            frontendId,
                            userId,
                            userFullName,
                        });

                        // Propagate this message to all backends, to ensure everyone has a correct internal state
                        userService.multicast({...req.body(), uuid: backendId}).catch((e) => {
                            console.error('Error sharing message:', e);
                        });
                    } catch (e) {
                        console.error('Error handling subscription:', e);
                    }
                    break;

                case ServiceRequestType.PUBLISH:
                    if (topic === USER_SERVICE_TOPICS.COLLECT_STATE || topic === USER_SERVICE_TOPICS.COLLECT_STATE_REPLY) {
                        handleCollect(req.body());
                        return;
                    }

                    // Prevent message loops (multicast)
                    if (!uuid) {
                        // If it is not a multicast message, send multicast to update other backends
                        userService.multicast({...req.body(), uuid: backendId}).catch((e) => {
                            console.error('Error sharing message:', e);
                        });
                    } else if (uuid === backendId) {
                        // If the message comes from the same backend, ignore it
                        return;
                    }

                    // If we receive a multicast from another backend, and the message is a "subscribe", we only need
                    // to maintain the user info
                    if (uuid && userId) {
                        frontendIdUserLink.set(frontendId, {userId, userFullName});
                        recordUserServiceStartupMutation(frontendsCheckingAPR, {
                            op: SYNC_MUTATION.SET_USER_LINK,
                            frontendId,
                            userId,
                            userFullName,
                        });
                        return;
                    }

                    // If we receive a multicast from another backend, and the message is a "unsubscribe", we need to
                    // remove the user from the state
                    if (uuid && frontendWhoUnsubscribed) {
                        removeFrontendFromPrLists(frontendsCheckingAPR, frontendWhoUnsubscribed);
                        recordUserServiceStartupMutation(frontendsCheckingAPR, {
                            op: SYNC_MUTATION.UNSUBSCRIBE_FRONTEND,
                            frontendId: frontendWhoUnsubscribed,
                        });
                        return;
                    }

                    // If we receive a "unlock all PRs" message, we simply clear the "frontends checking a PR" Map
                    if (topic === USER_SERVICE_TOPICS.UNLOCK_ALL_PRS) {
                        frontendsCheckingAPR.clear();
                        recordUserServiceStartupMutation(frontendsCheckingAPR, {op: SYNC_MUTATION.UNLOCK_ALL_PRS});
                        return;
                    }

                    try {
                        // When a PR is opened or closed, update who is checking it.
                        applyOpenPrs(frontendsCheckingAPR, frontendId, openPrs);
                        recordUserServiceStartupMutation(frontendsCheckingAPR, {
                            op: SYNC_MUTATION.OPEN_PRS,
                            frontendId,
                            openPrs: openPrs.slice(),
                        });

                        // Get all the frontends impacted by the change to notify them
                        const impactedFrontends = frontendsCheckingAPR.get(updatedPr) || [];

                        // If it is a multicast message,
                        // no need to reply (we don't want to send a message to a frontend multiple times).
                        if (!uuid) {
                            for (const frontendIdUser of impactedFrontends) {
                                // Reply with updated state
                                await Service.publish(`SubscribedPR_${frontendIdUser}`, {
                                    frontendsCheckingAPR: frontendsCheckingAPR,
                                    frontendIdUserLink: frontendIdUserLink,
                                });
                            }
                        }
                    } catch (e) {
                        console.error('Error handling publish:', e);
                        console.error(`Frontends checking a PR for updated PR (${updatedPr}): ${frontendsCheckingAPR.get(updatedPr)}.`);
                    }
                    break;

                case ServiceRequestType.GET:
                    if (topic === USER_SERVICE_TOPICS.GET_USER_INFO) {
                        const frontendIds = [];
                        frontendIdUserLink.forEach((value, key) => {
                            if (value.userId === userId) {
                                frontendIds.push(key);
                            }
                        });
                        const onlineFrontendsRelatedToUser = [];
                        onlineFrontends.forEach((value, key) => {
                            if (frontendIds.includes(value)) {
                                onlineFrontendsRelatedToUser.push(value);
                            }
                        });
                        const userIsCheckingThesePRs = [];
                        frontendsCheckingAPR.forEach((value, key) => {
                            if (value.some((id) => frontendIds.includes(id))) {
                                userIsCheckingThesePRs.push(key);
                            }
                        });

                        req.reply({
                            userId: userId,
                            backendId: backendId,
                            onlineFrontendsRelatedToUser: onlineFrontendsRelatedToUser,
                            userIsCheckingThesePRs: userIsCheckingThesePRs,
                            frontendIds: frontendIds,
                        });
                    } else if (topic === USER_SERVICE_TOPICS.SYNCHRONIZE) {
                        if (initiator !== backendId) {
                            // Reply with this replica's shared state. onlineFrontends stays local.
                            req.reply({
                                ...getSyncSnapshot(),
                                uuid: backendId,
                                peerCount: 1,
                            });
                        } else {
                            // This replica received the sync request. Collect state from peers.
                            try {
                                const snapshots = await collectPeerSnapshots({
                                    multicast: (body) => userService.multicast(body),
                                    requestBody: {topic: USER_SERVICE_TOPICS.COLLECT_STATE},
                                    selfId: backendId,
                                    selfSnapshot: getSyncSnapshot(),
                                    expected: MAX_EXPECTED_REPLICAS,
                                    timeoutMs: COLLECT_MEMORIES_TIMEOUT_MS,
                                    peerGraceMs: SYNC_ECHO_GRACE_MS,
                                    pendingCollections,
                                });
                                const merged = {
                                    frontendsCheckingAPR: /** @type {Map<string, string[]>} */ (new Map()),
                                    frontendIdUserLink: /** @type {Map<string, {userId: string, userFullName: string}>} */ (new Map()),
                                };
                                const result = applyUserServicePeerSnapshots(backendId, snapshots, merged);
                                if (result.peerCount === 0) {
                                    req.reply({ignore: true, peerCount: 0});
                                } else {
                                    req.reply({
                                        frontendsCheckingAPR: merged.frontendsCheckingAPR,
                                        frontendIdUserLink: merged.frontendIdUserLink,
                                        backendId,
                                        uuid: backendId,
                                        peerCount: result.peerCount,
                                    });
                                }
                            } catch (e) {
                                console.error('Error collecting user service state from peers:', e);
                                req.reply({ignore: true, peerCount: 0});
                            }
                        }
                    } else {
                        req.reply({
                            backendId: backendId,
                            onlineFrontends: onlineFrontends,
                            frontendsCheckingAPR: frontendsCheckingAPR,
                            frontendIdUserLink: frontendIdUserLink,
                        });
                    }
                    break;
                }
            }),
        ).subscribe();

        // Triggered when a frontend unsubscribes
        userService.setUnsubscriptionHandler(async (subId) => {
            const frontendWhoUnsubscribed = onlineFrontends.get(subId);

            // Clean up state
            onlineFrontends.delete(subId);

            const updatedPrs = removeFrontendFromPrLists(frontendsCheckingAPR, frontendWhoUnsubscribed);
            if (frontendWhoUnsubscribed) {
                recordUserServiceStartupMutation(frontendsCheckingAPR, {
                    op: SYNC_MUTATION.UNSUBSCRIBE_FRONTEND,
                    frontendId: frontendWhoUnsubscribed,
                });
            }

            // Get all the frontends impacted by the change to notify them
            const impactedFrontends = updatedPrs.flatMap((pr) => frontendsCheckingAPR.get(pr) || []);

            await userService.multicast({uuid: backendId, frontendWhoUnsubscribed: frontendWhoUnsubscribed}).catch((e) => {
                console.error('Error sharing message:', e);
            });

            // If it is a multicast message,
            // no need to reply (we don't want to send a message to a frontend multiple times).
            for (const frontendIdUser of impactedFrontends) {
                // Reply with updated state
                await Service.publish(`SubscribedPR_${frontendIdUser}`, {
                    frontendsCheckingAPR: frontendsCheckingAPR,
                    frontendIdUserLink: frontendIdUserLink,
                });
            }
        });

        setFrontendsCheckingAPR(frontendsCheckingAPR);
        setFrontendIdUserLink(frontendIdUserLink);
        setUniqueID(backendId);
        forwardEvent();
    }
}

export const USER_SERVICE_TOPICS = {
    UNLOCK_ALL_PRS: 'unlock_all_prs',
    GET_USER_INFO: 'get_user_info',
    SYNCHRONIZE: 'synchronize',
    COLLECT_STATE: 'collect_state',
    COLLECT_STATE_REPLY: 'collect_state_reply',
};

registerBrick('018d5af30fcf92c05e29', CreateUserService);
